/**
 * ApiProvider — inicializa o cliente HTTP e o QueryClient (@tanstack/react-query).
 *
 * Ordem dos providers no main.jsx:
 *   BrowserRouter > AuthProvider > ApiProvider > QueryClientProvider > AppRouter
 *
 * Por que ApiProvider está abaixo de AuthProvider:
 *   O createApiClient(getToken) recebe getToken do AuthContext. Ao montar,
 *   o token na memória pode ser null (antes do restore); como getToken é
 *   chamado a cada fetch (não em cache), tokens renovados são usados
 *   automaticamente sem precisar recriar o cliente.
 *
 * Tratamento de 'nava:session-expired':
 *   Quando o backend retorna 401 em mid-request, o evento dispara o logout
 *   via AuthContext, limpando tokens e redirecionando para /login.
 */
import { useMemo, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { createApiClient } from './client';
import { ApiContext } from './ApiContext';

/**
 * Cria o QueryClient com defaults conservadores para a Fase 0.
 * Fase 1+: ajustar staleTime por query conforme os endpoints se estabilizarem.
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 30s de stale — adequado para dados de dashboard
        staleTime: 30_000,
        // 5 minutos no cache após unmount
        gcTime: 5 * 60_000,
        // Não refetch automático ao focar janela (evita flicker no Fase 0 com mocks)
        refetchOnWindowFocus: false,
        // 1 retry (default 3 é excessivo para erros 4xx)
        retry: (failureCount, error) => {
          // Não retentar 4xx (exceto 429)
          if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        // Sem retry automático em mutations (evita double-submit)
        retry: 0,
      },
    },
  });
}

export function ApiProvider({ children }) {
  const { getToken, logout } = useAuth();

  // QueryClient é estável (criado uma vez por montagem do provider)
  const queryClientRef = useRef(null);
  if (!queryClientRef.current) {
    queryClientRef.current = createQueryClient();
  }

  // Cria o cliente HTTP — estável enquanto getToken for a mesma referência
  const client = useMemo(() => createApiClient(getToken), [getToken]);

  // Ouve evento de sessão expirada (401 em mid-request)
  useEffect(() => {
    function handleSessionExpired() {
      // Invalida todas as queries em cache e faz logout
      queryClientRef.current?.clear();
      logout();
    }

    window.addEventListener('nava:session-expired', handleSessionExpired);
    return () => window.removeEventListener('nava:session-expired', handleSessionExpired);
  }, [logout]);

  return (
    <ApiContext.Provider value={{ client, _getToken: getToken }}>
      <QueryClientProvider client={queryClientRef.current}>
        {children}
      </QueryClientProvider>
    </ApiContext.Provider>
  );
}
